import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useRouteMatch } from 'react-router-dom';

import { Conditional } from 'common/components/Conditional/Conditional';
import { selectRequestedAppointment } from 'pages/checkout/connect/legacy-selectors';
import { useProviderOrPracticeDetails } from 'pages/checkout/hooks/useProviderOrPracticeDetails';
import { formatClinicAddress } from 'pages/checkout/utils/addressUtils';
import { formatTimeSlot } from 'pages/checkout/utils/timeUtils';
import { selectDataClinic } from 'pages/profile/connect/selectors';

import { ExperimentName, EXPERIMENTS } from 'utils/constants/experiment-configs';
import { useExperiment } from 'utils/customHooks/useExperiment';

import { AppointmentTime, ClinicAddress, ClinicAddressSkeleton, ClinicName, ClinicNameSecondary, ClinicNameSkeleton, ProfilePhoto, ProfilePhotoSkeleton, Wrapper } from './AppointmentDetails-styled-components';

export const AppointmentDetails = () => {
  const { isVariant: isSecurityMessagingVariant } = useExperiment({
    experiment: EXPERIMENTS[ExperimentName.CHECKOUT_SECURITY_MESSAGING],
  });
  const clinic = useSelector(selectDataClinic);
  const { params } = useRouteMatch();
  const { clinicId: clinicIdString } = params as { clinicId: string; };
  const clinicId = parseInt(clinicIdString, 10);
  const { slotId } = useSelector(selectRequestedAppointment);

  const { date: slotDate, time: slotTime } = useMemo(() => {
    const slot = clinic.slots?.find((slot: any) => slot.id === slotId);
    return slot || {};
  }, [clinic.slots, slotId]);

  // If results/:clinicId/ doesn't match what we've loaded to Redux, we're loading it
  const isLoadingClinicData = useMemo(() => {
    return clinic.id !== clinicId;
  }, [clinic.id, clinicId]);

  const formattedAddress = formatClinicAddress(clinic.address);
  const formattedTimeSlot = formatTimeSlot(slotDate, slotTime);

  const isInstantBook = Boolean(slotDate && slotTime);

  const { photoUrl: profilePhotoUrl, providerName, practiceName, showProvider } = useProviderOrPracticeDetails();

  if (isLoadingClinicData) {
    return (
      <Wrapper>
        <ProfilePhotoSkeleton />
        <ClinicNameSkeleton />
        <ClinicAddressSkeleton />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <ProfilePhoto src={profilePhotoUrl} alt="" />
      <ClinicName>{showProvider ? providerName : practiceName}</ClinicName>
      <Conditional condition={showProvider}>
        <ClinicNameSecondary>{practiceName}</ClinicNameSecondary>
      </Conditional>
      <ClinicAddress>{formattedAddress}</ClinicAddress>
      {isInstantBook
        && <AppointmentTime className="sensitive-data" $isSecurityMessagingVariant={isSecurityMessagingVariant}>{formattedTimeSlot}</AppointmentTime>}
    </Wrapper>
  );
};
